import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ErpConnectionView, ErpOverview, ErpProviderInfo } from '../../api';
import { api, erpApi, wordpressApi } from '../../api';
import { useResource } from '../../hooks';
import { ErpFlow } from '../../components/erp/ErpFlow';
import { ErpInventoryPanel } from '../../components/erp/ErpInventoryPanel';
import { ErpMappingPanel } from '../../components/erp/ErpMappingPanel';
import type { MatchingViewPatch } from '../../components/erp/ErpMappingPanel';
import { ErpIssuesPanel, ErpRunsPanel } from '../../components/erp/ErpActivityPanels';
import { ErpDocumentsPanel } from '../../components/erp/ErpDocumentsPanel';
import { ErpSettingsPanel } from '../../components/erp/ErpSettingsPanel';
import { ErpStoresPanel } from '../../components/erp/ErpStoresPanel';
import type { ErpSetupStep } from '../../components/erp/labels';
import {
  connectionStatusLabel,
  connectionStatusLevel,
  erpSetupSteps,
  erpSyncHealth,
  erpTabCount,
  failureClassLabel,
  labelOr,
  formatNumber,
  linkedWebsites,
  untilWhen,
} from '../../components/erp/labels';
import { syncStateLabel } from '../../components/erp/ErpFlow';
import { magentoStoreCopy, StoreCopyContext, useStoreCopy, wooCommerceStoreCopy } from '../../components/erp/storeCopy';
import { useAction } from '../../components/erp/useAction';
import { relativeWhen } from '../../components/distribution/labels';
import { LevelBadge, PanelCard, ProjectSection, SectionHeader } from '../../components/project/ui';
import { Checklist, Field, HelpTip, Input, Select, Tabs } from '../../components/primitives';
import { EvidenceActions, NextSteps, OperationSummary, SummaryFact, SummaryFacts } from '../../components/operation/OperationSummary';
import type { NextStep } from '../../components/operation/OperationSummary';
import { actionLabel } from '../../components/operation/actionLabels';
import { Timestamp } from '../../components/status/StatusPieces';
import { ErrorNotice, SkeletonPage } from '../../components/ui';
import { useProject } from './ProjectContext';

type Tab = 'overview' | 'inventory' | 'matching' | 'documents' | 'issues' | 'history' | 'stores' | 'settings';

const tabs: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'matching', label: 'Product matching' },
  { id: 'documents', label: 'Invoices' },
  { id: 'issues', label: 'Issues' },
  { id: 'history', label: 'Sync history' },
  { id: 'stores', label: 'Stores' },
  { id: 'settings', label: 'Settings' },
];

/**
 * ERP integrations (CO-77): the ERP owns stock and prices, Commerce Ops
 * reads them and sends each connected store what changed. A store never
 * talks to the ERP and never holds its key.
 */
export function ErpSection() {
  const project = useProject();
  // A WooCommerce store runs the Commerce Ops plugin, not the Magento
  // modules: every sentence about the store's admin follows (CO-137).
  return (
    <StoreCopyContext.Provider value={project.isWordPress ? wooCommerceStoreCopy : magentoStoreCopy}>
      <ErpSectionBody />
    </StoreCopyContext.Provider>
  );
}

function ErpSectionBody() {
  const project = useProject();
  const words = useStoreCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reloadToken, setReloadToken] = useState(0);
  const refresh = useCallback(() => setReloadToken((value) => value + 1), []);

  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const overview = useResource(
    () => erpApi.overview(project.projectId),
    [project.projectId, reloadToken],
    project.projectId, { key: `project:${project.projectId}:erp-overview` },
  );

  // The WordPress sites Commerce Ops reaches: there it installs the plugin
  // itself (CO-137). A failed read only hides that offer.
  const sites = useResource(
    () => (project.isWordPress ? wordpressApi.listSites(project.projectId) : Promise.resolve({ sites: [] })),
    [project.projectId, project.isWordPress],
    project.projectId, { key: `project:${project.projectId}:wordpress-sites:${project.isWordPress}` },
  );
  const hostedEnvironmentIds = useMemo(
    () => (sites.data?.sites ?? []).map((site) => site.environment_id),
    [sites.data],
  );

  const identity = workspace.data?.identity;
  // The server re-checks every mutation; this only decides whether a
  // control is offered, so a read-only member is not shown buttons that
  // would be refused.
  const canManage = Boolean(
    identity?.role?.is_owner_role ||
      identity?.capabilities?.includes('manage_erp_integrations') ||
      identity?.kind === 'local_development_workspace',
  );

  const viewsRef = useRef<HTMLDivElement>(null);
  // Stuck at the top, the tabs draw a line under themselves so the view
  // does not seem cut off (CO-158). A zero-height marker just above them
  // leaves the viewport exactly when they stick.
  const [stuck, setStuck] = useState(false);
  const stuckObserver = useRef<IntersectionObserver | null>(null);
  const watchStuck = useCallback((marker: HTMLDivElement | null) => {
    stuckObserver.current?.disconnect();
    stuckObserver.current = null;
    if (!marker || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(Boolean(entry && !entry.isIntersecting && entry.boundingClientRect.top < 1)),
      { rootMargin: '-1px 0px 0px 0px', threshold: 0 },
    );
    observer.observe(marker);
    stuckObserver.current = observer;
  }, []);
  // Product matching keeps its website, group, search and open product in the
  // address (CO-164): choosing a product is an entry of its own, the rest
  // replaces the entry.
  const navigate = useNavigate();
  const matchingView = {
    store: searchParams.get('store') ?? undefined,
    q: searchParams.get('q') ?? undefined,
    state: searchParams.get('state') ?? undefined,
    sku: searchParams.get('sku') ?? undefined,
  };
  const changeMatchingView = (patch: MatchingViewPatch, mode: 'push' | 'replace' | 'back') => {
    if (mode === 'back') {
      navigate(-1);
      return;
    }
    // One change, one call: a second call from the same render would drop the first.
    const params = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === undefined || value === '') params.delete(key);
      else params.set(key, value);
    }
    setSearchParams(params, { replace: mode === 'replace' });
  };
  const setTab = (next: Tab) => {
    const params = new URLSearchParams(searchParams);
    params.delete('q');
    params.delete('store');
    params.delete('state');
    params.delete('sku');
    params.delete('run');
    if (next === 'overview') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
    // When the reader has scrolled past the start of the views (the tabs
    // are stuck under the header), open the new view at its top rather
    // than somewhere in its middle. Otherwise the page stays where it is.
    const views = viewsRef.current;
    if (views && views.getBoundingClientRect().top < 0) views.scrollIntoView({ block: 'start' });
  };

  if (overview.loading && !overview.data) return <SkeletonPage cards={4} rows={4} />;
  if (overview.error || !overview.data) {
    return (
      <ProjectSection>
        <ErrorNotice error={overview.error ?? new Error('ERP integrations could not be loaded.')} />
      </ProjectSection>
    );
  }

  const data: ErpOverview = overview.data;
  const connectionId = searchParams.get('connection');
  const connection = data.connections.find((entry) => entry.id === connectionId) ?? data.connections[0] ?? null;

  if (!connection) {
    return (
      <ProjectSection label="ERP integrations">
        <ConnectErp
          projectId={project.projectId}
          providers={data.providers}
          canManage={canManage}
          onCreated={refresh}
        />
        <ErpStoresPanel
          projectId={project.projectId}
          projectName={project.project.name}
          environments={project.environments}
          stores={data.stores}
          connection={null}
          hostedEnvironmentIds={hostedEnvironmentIds}
          canManage={canManage}
          onChanged={refresh}
        />
      </ProjectSection>
    );
  }

  // Invoices are offered only for an ERP that can take them.
  const takesDocuments = connection.provider_info.capabilities.some((entry) => entry.operation === 'submit_invoice' && entry.implemented);
  const visibleTabs = tabs.filter((entry) => entry.id !== 'documents' || takesDocuments);
  const tab = (visibleTabs.find((entry) => entry.id === searchParams.get('tab'))?.id ?? 'overview') as Tab;

  const steps = erpSetupSteps(connection, data.stores, words);
  const setupDone = steps.every((step) => step.done);

  return (
    <ProjectSection label="ERP integrations">
      {/* One compact header on every view (CO-158): name, provider, state,
          the last and next sync and the one primary action. */}
      <ConnectionHeader
        connection={connection}
        steps={tab === 'overview' ? null : steps}
        canManage={canManage}
        onChanged={refresh}
        onGo={setTab}
        onViewError={(runId) => {
          const params = new URLSearchParams(searchParams);
          params.set('tab', 'history');
          if (runId) params.set('run', runId);
          else params.delete('run');
          setSearchParams(params);
          const views = viewsRef.current;
          if (views && views.getBoundingClientRect().top < 0) views.scrollIntoView({ block: 'start' });
        }}
      />

      {/* The tabs stay put while the views below change height (observation
          6): they stick under the top of the viewport (the phone topbar
          below 768px), and the panel is at least one viewport tall so a
          short view never shrinks the page under the reader. */}
      <div className="erp-views" ref={viewsRef}>
      <div className="erp-views__marker" ref={watchStuck} aria-hidden="true" />
      <div className="erp-views__tabs" data-stuck={stuck ? 'true' : undefined}>
      <Tabs
        label="ERP integration views"
        variant="primary"
        value={tab}
        onChange={setTab}
        panelId="erp-view-panel"
        idBase="erp-tab"
        options={visibleTabs.map((entry) => {
          const open = erpTabCount(entry.id, connection);
          return {
            value: entry.id,
            // The count reads as "Issues, open: 3" to a screen reader.
            label:
              open > 0 ? (
                <>
                  {entry.label}
                  <span className="visually-hidden">{entry.id === 'documents' ? ', needing attention:' : ', open:'}</span>
                </>
              ) : (
                entry.label
              ),
            count: open > 0 ? open : undefined,
            testId: `erp-tab-${entry.id}`,
          };
        })}
      />
      </div>

      <div className="erp-views__panel" id="erp-view-panel" role="tabpanel" aria-labelledby={`erp-tab-${tab}`}>
      {tab === 'overview' ? (
        <>
          {setupDone ? null : <SetupChecklist steps={steps} onGo={setTab} />}
          <ErpFlow connection={connection} />
          <SummaryRow connection={connection} />
          <ErpIssuesPanel connection={connection} canManage={canManage} onChanged={refresh} limit={4} onShowAll={() => setTab('issues')} />
          <ErpRunsPanel connection={connection} limit={5} onShowAll={() => setTab('history')} />
        </>
      ) : null}
      {tab === 'inventory' ? <ErpInventoryPanel key={searchParams.get('q') ?? ''} connection={connection} initialQuery={searchParams.get('q') ?? ''} /> : null}
      {tab === 'matching' ? (
        <ErpMappingPanel
          connection={connection}
          canManage={canManage}
          view={matchingView}
          onViewChange={changeMatchingView}
          onOpenStores={() => setTab('stores')}
        />
      ) : null}
      {tab === 'documents' ? <ErpDocumentsPanel connection={connection} canManage={canManage} onChanged={refresh} /> : null}
      {tab === 'issues' ? <ErpIssuesPanel connection={connection} canManage={canManage} onChanged={refresh} /> : null}
      {tab === 'history' ? <ErpRunsPanel connection={connection} openRunId={searchParams.get('run') ?? undefined} /> : null}
      {tab === 'stores' ? (
        <ErpStoresPanel
          projectId={project.projectId}
          projectName={project.project.name}
          environments={project.environments}
          stores={mergeStores(data, connection)}
          connection={connection}
          connectionNames={Object.fromEntries(data.connections.map((c) => [c.id, c.name]))}
          hostedEnvironmentIds={hostedEnvironmentIds}
          canManage={canManage}
          onChanged={refresh}
        />
      ) : null}
      {tab === 'settings' ? (
        <ErpSettingsPanel
          connection={connection}
          canManage={canManage}
          onChanged={refresh}
          onDeleted={() => {
            setTab('overview');
            refresh();
          }}
        />
      ) : null}
      </div>
      </div>
    </ProjectSection>
  );
}

/** The project's own stores plus any store of another project this connection serves. */
function mergeStores(data: ErpOverview, connection: ErpConnectionView) {
  const byId = new Map(data.stores.map((store) => [store.id, store]));
  for (const store of connection.stores) byId.set(store.id, store);
  return [...byId.values()];
}

function capitalise(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function nextSyncLabel(connection: ErpConnectionView): string {
  if (!connection.credential_configured) return 'After the access key is stored';
  if (!connection.enabled) return 'Schedule off';
  return capitalise(untilWhen(connection.next_stock_sync_at));
}

function ConnectionHeader({
  connection,
  steps,
  canManage,
  onChanged,
  onGo,
  onViewError,
}: {
  connection: ErpConnectionView;
  /** The setup steps, for the one-line setup state; null where the full checklist is shown. */
  steps: ErpSetupStep[] | null;
  canManage: boolean;
  onChanged: () => void;
  onGo: (tab: Tab) => void;
  /** Opens the sync history at the failed run, when its id is known. */
  onViewError: (runId?: string) => void;
}) {
  const action = useAction();
  const ready = connection.credential_configured && connection.enabled;
  const next = steps?.find((step) => !step.done) ?? null;
  const provider = connection.provider_info.display_name || connection.provider;
  const syncProblem = erpSyncHealth(connection).kind === 'stale' || erpSyncHealth(connection).kind === 'failed';
  const requestSync = () =>
    void action.run(
      'sync',
      async () => {
        await erpApi.requestSync(connection.id);
        onChanged();
      },
      'Sync requested.',
    );
  return (
    <header className="erp-header" data-testid="erp-header">
      <div className="erp-header__top">
        <div className="erp-header__title">
          <h2 className="erp-header__name">{connection.name}</h2>
          <p className="erp-header__provider">
            {provider}
            {connection.company_identifier ? ` · ${connection.company_identifier}` : ''}
            {connection.settings.test_company ? (
              <>
                {' · '}
                <LevelBadge level="unknown">Test company</LevelBadge>
              </>
            ) : null}
          </p>
        </div>
        <dl className="erp-header__facts">
          <div>
            <dt>Connection</dt>
            <dd>
              <LevelBadge level={connectionStatusLevel[connection.status]}>{connectionStatusLabel[connection.status]}</LevelBadge>
            </dd>
          </div>
          <div>
            <dt>Last successful sync</dt>
            <dd data-testid="erp-last-sync">
              {connection.last_stock_sync_success_at ? <Timestamp iso={connection.last_stock_sync_success_at} /> : 'Not synced yet'}
            </dd>
          </div>
          <div>
            <dt>Next scheduled sync</dt>
            <dd data-testid="erp-next-sync">{nextSyncLabel(connection)}</dd>
          </div>
        </dl>
        {canManage && !syncProblem ? (
          <button
            type="button"
            className="button button--secondary"
            disabled={!ready || action.busy !== null}
            title={ready ? undefined : connection.credential_configured ? 'Turn on “Sync stock on schedule” in Settings first' : 'Store the access key first'}
            data-testid="erp-sync-now"
            onClick={requestSync}
          >
            {action.busy === 'sync' ? 'Requesting' : 'Sync stock'}
          </button>
        ) : null}
      </div>
      {syncProblem ? (
        <SyncProblem connection={connection} canManage={canManage} ready={ready} busy={action.busy === 'sync'} disabled={action.busy !== null} onRetry={requestSync} onViewError={onViewError} />
      ) : null}
      {next && steps ? (
        <p className="erp-header__setup" data-testid="erp-setup-line">
          <span aria-label={`Setup: ${steps.filter((step) => step.done).length} of ${steps.length} steps done. Next: ${next.label}`}>
            Setup {steps.filter((step) => step.done).length}/{steps.length} · Next: {next.label}
          </span>
          <button type="button" className="button button--secondary button--small" onClick={() => onGo(next.tab)}>
            Continue setup
          </button>
        </p>
      ) : null}
      <ErrorNotice error={action.error} />
      {action.notice ? (
        <p className="panel__note" role="status">
          {action.notice}
        </p>
      ) : null}
    </header>
  );
}

/** What to do about a failed sync, only from the class the sync recorded. */
function syncNextSteps(connection: ErpConnectionView): NextStep[] {
  const failed = connection.last_run?.state === 'failed' ? connection.last_run : null;
  const next = connection.enabled && connection.next_stock_sync_at ? untilWhen(connection.next_stock_sync_at) : null;
  switch (failed?.error_class) {
    case 'auth':
      return [{ label: 'Replace the access key in Settings, then retry', to: '?tab=settings' }];
    case 'transient':
    case 'rate_limited':
    case 'busy':
      return next
        ? [{ label: `Wait for the next scheduled sync (${next})`, detail: 'Retry runs it now.' }]
        : [{ label: 'Retry the sync' }];
    case 'permanent':
      return [{ label: 'Read what the ERP answered in the error' }];
    default:
      return [];
  }
}

/**
 * SyncProblem is the one place a stale or failed stock sync is told (UX
 * architecture rule D): the conclusion, what it means for the stores, the
 * cause as far as it is known, the next step, and the buttons that open the
 * error and run the sync again. The flow node, the tiles and the run history
 * keep their own short state; they do not repeat this block.
 */
function SyncProblem({
  connection,
  canManage,
  ready,
  busy,
  disabled,
  onRetry,
  onViewError,
}: {
  connection: ErpConnectionView;
  canManage: boolean;
  ready: boolean;
  busy: boolean;
  disabled: boolean;
  onRetry: () => void;
  onViewError: (runId?: string) => void;
}) {
  const health = erpSyncHealth(connection);
  const failedRun = connection.last_run?.state === 'failed' ? connection.last_run : null;
  const lastFailed = connection.last_stock_sync_status === 'failed';
  const title = health.kind === 'stale' ? 'Stock is out of date' : syncStateLabel(connection);
  const cause = connection.last_stock_sync_detail || failedRun?.error_message || '';
  const steps = syncNextSteps(connection);
  return (
    <OperationSummary
      tone={health.kind === 'stale' ? 'danger' : 'warning'}
      title={title}
      testId="erp-sync-problem"
      actions={
        <>
          <EvidenceActions onViewError={lastFailed || failedRun ? () => onViewError(failedRun?.id) : undefined} errorTestId="erp-sync-view-error" />
          {canManage ? (
            <button type="button" className="button button--secondary button--small" disabled={!ready || disabled} onClick={onRetry} data-testid="erp-sync-now">
              {busy ? 'Requesting' : actionLabel.retry}
            </button>
          ) : null}
        </>
      }
    >
      <SummaryFacts>
        <SummaryFact label="Last success">{relativeWhen(connection.last_stock_sync_success_at)}</SummaryFact>
        <SummaryFact label="Stores">
          Keep their current stock{lastFailed ? ' · nothing changed' : ''}
        </SummaryFact>
        {lastFailed ? (
          <SummaryFact label="Cause" testId="erp-sync-cause">
            {failedRun?.error_class ? `${labelOr(failureClassLabel, failedRun.error_class)}: ` : ''}
            {cause || 'Not recorded'}
          </SummaryFact>
        ) : null}
        <SummaryFact label="Next step" testId="erp-sync-next">
          <NextSteps
            steps={steps}
            unknown={lastFailed ? 'The cause was not classified. Open the error to read what the sync recorded.' : 'No sync has succeeded recently. Retry, or check the schedule in Settings.'}
          />
        </SummaryFact>
      </SummaryFacts>
    </OperationSummary>
  );
}

/**
 * SetupChecklist lists what is left before stock reaches a store. Setup is
 * a real sequence, which is why its steps are numbered; the list disappears
 * once every step is done.
 */
function SetupChecklist({ steps, onGo }: { steps: ErpSetupStep[]; onGo: (tab: Tab) => void }) {
  const next = steps.findIndex((step) => !step.done);
  return (
    <PanelCard labelledBy="erp-checklist-title" tone="warning" testId="erp-checklist">
      <SectionHeader
        id="erp-checklist-title"
        title="Finish setting up"
        description="Nothing is sent to a store until every step is done."
      />
      <Checklist
        label="Steps before anything is sent"
        items={steps.map((step, index) => ({
          key: step.label,
          label: step.label,
          state: step.done ? 'done' : 'todo',
          action:
            index === next ? (
              <button type="button" className="button button--secondary button--small" onClick={() => onGo(step.tab)}>
                Go to {step.tab === 'stores' ? 'Stores' : 'Settings'}
              </button>
            ) : undefined,
        }))}
      />
    </PanelCard>
  );
}

/**
 * SummaryRow: the four numbers of the connection, once (CO-158). Each
 * says what it counts; the last sync is in the header.
 */
function SummaryRow({ connection }: { connection: ErpConnectionView }) {
  const run = connection.last_run;
  const issueCount = connection.issues.blocking + connection.issues.warning;
  const websites = linkedWebsites(connection);
  const stats = run?.kind === 'stock' && run.state === 'succeeded' ? run.stats : null;
  const changes = stats ? stats.items_changed + stats.prices_changed : null;
  return (
    <dl className="erp-stats" data-testid="erp-summary">
      <div className="erp-stats__cell">
        <dt>
          Inventory positions
          <HelpTip label="What is an inventory position?">
            One article in one warehouse. The same article in multiple warehouses counts separately.
          </HelpTip>
        </dt>
        <dd className="erp-stats__value tabular-nums">{formatNumber(connection.positions)}</dd>
        <dd className="erp-stats__meta">
          In {connection.warehouses.length} {connection.warehouses.length === 1 ? 'warehouse' : 'warehouses'}
        </dd>
      </div>
      <div className="erp-stats__cell">
        <dt>Connected websites</dt>
        <dd className="erp-stats__value tabular-nums">{websites.length}</dd>
        <dd className="erp-stats__meta">
          {connection.stores.length === 1 ? '1 store' : `${connection.stores.length} stores`}
        </dd>
      </div>
      <div className="erp-stats__cell">
        <dt>Changes in the last sync</dt>
        <dd className="erp-stats__value tabular-nums">{changes === null ? '—' : formatNumber(changes)}</dd>
        {/* A failed sync is told by the problem block above; the tile only shows no number. */}
        {stats || run?.state !== 'failed' ? (
          <dd className="erp-stats__meta">
            {stats ? `${formatNumber(stats.quantities_sent + stats.prices_sent + (stats.products_sent ?? 0))} updates sent` : 'No sync yet'}
          </dd>
        ) : null}
      </div>
      <div className="erp-stats__cell">
        <dt>Open issues</dt>
        <dd className={`erp-stats__value tabular-nums${connection.issues.blocking > 0 ? ' erp-stats__value--critical' : ''}`}>{issueCount}</dd>
        {connection.issues.blocking > 0 ? <dd className="erp-stats__meta">{connection.issues.blocking} block sending</dd> : null}
      </div>
    </dl>
  );
}

/** ConnectErp is the first screen: no ERP is connected to this project yet. */
function ConnectErp({
  projectId,
  providers,
  canManage,
  onCreated,
}: {
  projectId: string;
  providers: ErpProviderInfo[];
  canManage: boolean;
  onCreated: () => void;
}) {
  const action = useAction();
  const [provider, setProvider] = useState(providers[0]?.provider ?? '');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const info = providers.find((entry) => entry.provider === provider);

  return (
    <PanelCard labelledBy="erp-connect-title" testId="erp-connect">
      <SectionHeader id="erp-connect-title" title="Connect an ERP" />
      {canManage ? (
        <form
          className="erp-connect-form"
          onSubmit={(event) => {
            event.preventDefault();
            void action.run('create', async () => {
              await erpApi.createConnection(projectId, { provider, name: name.trim(), company_identifier: company.trim() });
              onCreated();
            });
          }}
        >
          <Field label="ERP">
            <Select id="erp-provider" value={provider} onChange={(event) => setProvider(event.target.value)}>
              {providers.map((entry) => (
                <option key={entry.provider} value={entry.provider}>
                  {entry.display_name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Connection name">
            <Input
              id="erp-connection-name"
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              placeholder="For example, the company’s name…"
              autoComplete="off"
            />
          </Field>
          <Field label={info?.company_identifier_label ?? 'Company'} hint={info?.company_identifier_hint || undefined}>
            <Input
              id="erp-company"
              value={company}
              maxLength={64}
              spellCheck={false}
              autoComplete="off"
              onChange={(event) => setCompany(event.target.value)}
            />
          </Field>
          <button
            type="submit"
            className="button button--primary"
            disabled={!provider || !name.trim() || !company.trim() || action.busy !== null}
            data-testid="erp-create-connection"
          >
            {action.busy === 'create' ? 'Connecting' : 'Connect'}
          </button>
        </form>
      ) : (
        <p className="quiet-empty">Ask a project owner, or a member who manages ERP integrations, to connect the ERP.</p>
      )}
      <ErrorNotice error={action.error} />
    </PanelCard>
  );
}
